import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  CardContent,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  useTheme,
  Avatar,
  Stack,
  Tooltip,
  InputAdornment,
  Checkbox,
  Toolbar,
  alpha,
  TablePagination,
  Paper,
  Menu,
  ListItemText,
  ListItemIcon,
  TextField
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  SelectAll as SelectAllIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  ViewColumn as ViewColumnIcon,
  ContentCopy as ContentCopyIcon,
  Add as AddCircleIcon,
  Remove as RemoveCircleIcon,
  Edit as EditCircleIcon
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import { useNotification } from '../../contexts/NotificationContext';
import { multiTenantMembershipAPI } from '../../services/api';
import api from '../../services/api';
import walletMembersAPI, {
  WalletMember,
  MemberListFilters
} from '../../services/walletsAPI';
import ModernCard from '../../components/Common/ModernCard';
import ModernButton from '../../components/Common/ModernButton';
import ModernTextField from '../../components/Common/ModernTextField';
import ImageUploadWithCrop from '../../components/Common/ImageUploadWithCrop';
import IconContainer from '../../components/Common/IconContainer';
import ResponsivePageHeader from '../../components/Common/ResponsivePageHeader';
import { logger } from '../../utils/logger';

const WalletMembersAPI = walletMembersAPI;

// Column configuration type
interface TableColumn {
  id: string;
  label: string;
  visible: boolean;
}

// Member create/edit form component
const MemberDialog: React.FC<{
  open: boolean;
  member: WalletMember | null;
  programs: Array<{ id: string; name: string; }>;
  tiers: Array<{ id: string; name: string; }>;
  onClose: () => void;
  onSave: (member: WalletMember) => void;
}> = ({ open, member, programs, tiers: initialTiers, onClose, onSave }) => {
  const [formData, setFormData] = useState<Partial<WalletMember>>({
    programId: '',
    tierId: '',
    person: {
      forename: '',
      surname: '',
      emailAddress: '',
      mobileNumber: '',
      displayName: ''
    },
    points: 0,
    status: 'ENROLLED',
    optOut: false
  });

  const [templateFields, setTemplateFields] = useState<any[]>([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const [tiers, setTiers] = useState<Array<{ id: string; name: string; }>>(initialTiers);
  const [loadingTiers, setLoadingTiers] = useState(false);

  useEffect(() => {
    if (member) {
      // Editing existing member - populate all fields
      // Use our internal program_id (UUID) instead of PassKit programId
      const internalProgramId = (member as any).program_id || member.programId;

      setFormData({
        ...member,
        // Use internal program_id for dropdown (our UUID)
        programId: internalProgramId || programs[0]?.id || '',
        tierId: member.tierId || '',
        // Ensure status is set
        status: member.status || 'ENROLLED',
        // Ensure person object is complete
        person: {
          forename: member.person?.forename || '',
          surname: member.person?.surname || '',
          emailAddress: member.person?.emailAddress || '',
          mobileNumber: member.person?.mobileNumber || '',
          displayName: member.person?.displayName || '',
          ...member.person
        }
      });
    } else if (programs.length > 0) {
      // Creating new member OR dialog closed - reset to defaults
      setFormData({
        programId: programs[0]?.id || '',
        tierId: '',
        person: {
          forename: '',
          surname: '',
          emailAddress: '',
          mobileNumber: '',
          displayName: ''
        },
        points: 0,
        status: 'ENROLLED',
        optOut: false
      });
    }
  }, [member, programs]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open && !member) {
      // Dialog closed and no member being edited - reset form
      setFormData({
        programId: programs[0]?.id || '',
        tierId: '',
        person: {
          forename: '',
          surname: '',
          emailAddress: '',
          mobileNumber: '',
          displayName: ''
        },
        points: 0,
        status: 'ENROLLED',
        optOut: false
      });
      setTemplateFields([]);
    }
  }, [open, member, programs]);

  // Load tiers when programId changes in form data
  useEffect(() => {
    const loadTiersForProgram = async () => {
      if (!formData.programId) {
        setTiers([]);
        return;
      }

      setLoadingTiers(true);
      try {
        const response = await api.get(
          `/membership/programs/${formData.programId}/tiers`
        );

        if (response.data) {
          const tiersData = Array.isArray(response.data) ? response.data : response.data.tiers || [];
          setTiers(tiersData.map((t: any) => ({ id: t.id, name: t.name })));
        } else {
          setTiers([]);
        }
      } catch (error) {
        logger.error('Error loading tiers:', error);
        setTiers([]);
      } finally {
        setLoadingTiers(false);
      }
    };

    loadTiersForProgram();
  }, [formData.programId]);

  // Translation mapping for field labels
  const translateFieldLabel = (label: string): string => {
    const translations: { [key: string]: string } = {
      'External ID': 'Eksterni ID',
      'First Name': 'Ime',
      'Last Name': 'Prezime',
      'Email': 'Email',
      'Phone Number': 'Telefon',
      'Points': 'Poeni',
      'Status': 'Status',
      'Expiry Date': 'Datum Isticanja',
      'Profile Image': 'Profilna Slika',
      'Enrolled': 'Prijavljen',
      'Active': 'Aktivan',
      'Suspended': 'Suspendovan',
      'Cancelled': 'Otkazan',
      'ENROLLED': 'Prijavljen',
      'ACTIVE': 'Aktivan',
      'SUSPENDED': 'Suspendovan',
      'CANCELLED': 'Otkazan'
    };
    return translations[label] || label;
  };

  // Load template fields when tier changes
  useEffect(() => {
    const loadTemplateFields = async () => {
      if (!formData.tierId) {
        setTemplateFields([]);
        return;
      }

      setLoadingFields(true);
      try {
        const response = await api.get(
          `/membership/tiers/${formData.tierId}/template-fields`
        );

        if (response.data) {
          setTemplateFields(response.data.fields || []);
        } else {
          setTemplateFields([]);
        }
      } catch (error) {
        logger.error('Error loading template fields:', error);
        setTemplateFields([]);
      } finally {
        setLoadingFields(false);
      }
    };

    loadTemplateFields();
  }, [formData.tierId]);

  const handleSave = () => {
    // Validation
    if (!formData.programId || !formData.tierId || !formData.person?.emailAddress) {
      return;
    }

    // Generate display name if not provided
    if (formData.person && !formData.person.displayName) {
      formData.person.displayName = `${formData.person.forename || ''} ${formData.person.surname || ''}`.trim();
    }

    // Get PassKit programId - if we have original member, use its programId
    // Otherwise, formData.programId should already be correct
    const passkitProgramId = member?.programId || formData.programId;

    // Prepare complete member data with all required fields
    const completeMemberData: WalletMember = {
      ...formData,
      id: member?.id, // Include ID for updates
      externalId: formData.externalId || member?.externalId,
      programId: passkitProgramId, // Use PassKit program ID for API
      tierId: formData.tierId,
      status: formData.status || 'ENROLLED',
      points: formData.points || 0,
      optOut: formData.optOut || false,
      person: {
        ...formData.person,
        forename: formData.person?.forename || '',
        surname: formData.person?.surname || '',
        emailAddress: formData.person?.emailAddress || '',
        mobileNumber: formData.person?.mobileNumber || '',
        displayName: formData.person?.displayName || ''
      }
    } as WalletMember;

    onSave(completeMemberData);
  };

  // Helper function to get nested value from formData
  const getFieldValue = (fieldName: string): any => {
    if (fieldName.includes('.')) {
      const parts = fieldName.split('.');
      let value: any = formData;
      for (const part of parts) {
        value = value?.[part];
      }
      return value || '';
    }
    return (formData as any)[fieldName] || '';
  };

  // Helper function to set nested value in formData
  const setFieldValue = (fieldName: string, value: any) => {
    if (fieldName.includes('.')) {
      const parts = fieldName.split('.');
      const newFormData = { ...formData };
      let current: any = newFormData;

      for (let i = 0; i < parts.length - 1; i++) {
        if (!current[parts[i]]) {
          current[parts[i]] = {};
        }
        current = current[parts[i]];
      }

      current[parts[parts.length - 1]] = value;
      setFormData(newFormData);
    } else {
      setFormData({ ...formData, [fieldName]: value });
    }
  };

  // Render dynamic field based on type
  const renderField = (field: any) => {
    const value = getFieldValue(field.name);
    const translatedLabel = translateFieldLabel(field.label);

    if (field.type === 'select') {
      return (
        <Grid size={{ xs: 12, md: 6 }} key={field.name}>
          <FormControl fullWidth>
            <InputLabel>{translatedLabel}</InputLabel>
            <Select
              value={value || ''}
              label={translatedLabel}
              onChange={(e) => setFieldValue(field.name, e.target.value)}
            >
              {field.options?.map((option: any) => (
                <MenuItem key={option.value} value={option.value}>
                  {translateFieldLabel(option.label)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      );
    }

    if (field.type === 'image-upload') {
      const handleImageChange = (file: File | null, url: string | null) => {
        if (file && url) {
          // Convert File to base64 for backend
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64String = reader.result as string;
            setFieldValue(field.name, base64String);
          };
          reader.readAsDataURL(file);
        } else {
          setFieldValue(field.name, '');
        }
      };

      return (
        <Grid size={{ xs: 12 }} key={field.name}>
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              {translatedLabel}
              {!field.required && <Typography component="span" sx={{ color: 'text.secondary', ml: 1 }}>(opciono)</Typography>}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mb: 2, color: 'text.secondary' }}>
              {field.description || 'Slika će biti automatski crop-ovana na 512x512px'}
            </Typography>

            <ImageUploadWithCrop
              value={value && (value.startsWith('data:') || value.startsWith('http')) ? value : null}
              onChange={handleImageChange}
              label=""
              helperText="Slika će biti crop-ovana na 512x512px kvadrat"
              outputSize={{ width: 512, height: 512 }}
              aspectRatio={1}
              maxSizeInMB={5}
            />
          </Box>
        </Grid>
      );
    }

    if (field.type === 'date') {
      // For expiry date fields, ensure date is in the future
      const isExpiryField = field.name.toLowerCase().includes('expiry') ||
                           field.name.toLowerCase().includes('expire') ||
                           translatedLabel.toLowerCase().includes('isticanj') ||
                           translatedLabel.toLowerCase().includes('expir');

      return (
        <Grid size={{ xs: 12, md: 6 }} key={field.name}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack spacing={1}>
              <DatePicker
                label={translatedLabel}
                value={value ? dayjs(value) : null}
                minDate={isExpiryField ? dayjs() : undefined}
                onChange={(newValue) => {
                  if (newValue && dayjs.isDayjs(newValue)) {
                    // Validate that expiry date is in the future
                    if (isExpiryField && newValue.isBefore(dayjs(), 'day')) {
                      // Don't set past dates for expiry fields
                      return;
                    }
                    setFieldValue(field.name, newValue.format('YYYY-MM-DD'));
                  } else {
                    setFieldValue(field.name, '');
                  }
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: field.required,
                    helperText: isExpiryField
                      ? 'Datum mora biti u budućnosti'
                      : field.description,
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: 'background.paper',
                      }
                    }
                  }
                }}
              />
              {value && (
                <Button
                  size="small"
                  color="error"
                  onClick={() => setFieldValue(field.name, null)}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Obriši Datum
                </Button>
              )}
            </Stack>
          </LocalizationProvider>
        </Grid>
      );
    }

    return (
      <Grid size={{ xs: 12, md: 6 }} key={field.name}>
        <ModernTextField
          fullWidth
          label={translatedLabel}
          type={field.type}
          required={field.required}
          value={value}
          onChange={(e) => setFieldValue(field.name, e.target.value)}
          helperText={field.description}
        />
      </Grid>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {member ? 'Uredi Člana' : 'Novi Član'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {/* Program and Tier selection - always at the top */}
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Program</InputLabel>
              <Select
                value={formData.programId || ''}
                label="Program"
                onChange={(e) => setFormData({ ...formData, programId: e.target.value })}
              >
                {programs.map((program) => (
                  <MenuItem key={program.id} value={program.id}>
                    {program.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Tier</InputLabel>
              <Select
                value={formData.tierId || ''}
                label="Tier"
                onChange={(e) => setFormData({ ...formData, tierId: e.target.value })}
                disabled={loadingTiers || !formData.programId}
              >
                {loadingTiers ? (
                  <MenuItem value="">
                    <em>Učitavanje...</em>
                  </MenuItem>
                ) : tiers.length === 0 ? (
                  <MenuItem value="">
                    <em>Nema dostupnih tier-ova</em>
                  </MenuItem>
                ) : (
                  tiers.map((tier) => (
                    <MenuItem key={tier.id} value={tier.id}>
                      {tier.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>

          {/* Dynamic fields from template */}
          {loadingFields ? (
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            </Grid>
          ) : (
            templateFields.map((field) => renderField(field))
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Otkaži</Button>
        <ModernButton
          onClick={handleSave}
          disabled={!formData.programId || !formData.tierId || !formData.person?.emailAddress}
        >
          {member ? 'Ažuriraj' : 'Kreiraj'}
        </ModernButton>
      </DialogActions>
    </Dialog>
  );
};

const MembershipManagement: React.FC = () => {
  const theme = useTheme();
  const { handleApiError } = useErrorHandler();
  const { showSuccess, showError } = useNotification();

  // State
  const [members, setMembers] = useState<WalletMember[]>([]);
  const [filteredMembers, setFilteredMembers] = useState<WalletMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingMember, setEditingMember] = useState<WalletMember | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTier, setSelectedTier] = useState<string>('all');
  const [selectedMembers, setSelectedMembers] = useState<Set<string>>(new Set());

  // Default column configuration
  const defaultColumns: TableColumn[] = [
    { id: 'name', label: 'Ime', visible: true },
    { id: 'email', label: 'Email', visible: true },
    { id: 'phone', label: 'Telefon', visible: true },
    { id: 'externalId', label: 'External ID', visible: false },
    { id: 'tier', label: 'Tier', visible: true },
    { id: 'status', label: 'Status', visible: true },
    { id: 'points', label: 'Poeni', visible: false },
    { id: 'program', label: 'Program', visible: false },
    { id: 'created', label: 'Datum kreiranja', visible: false },
    { id: 'actions', label: 'Akcije', visible: true }
  ];

  // Load columns from localStorage or use defaults
  const [columns, setColumns] = useState<TableColumn[]>(() => {
    try {
      const saved = localStorage.getItem('memberTableColumns');
      return saved ? JSON.parse(saved) : defaultColumns;
    } catch {
      return defaultColumns;
    }
  });
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<null | HTMLElement>(null);

  // Points management state
  const [pointsDialogOpen, setPointsDialogOpen] = useState(false);
  const [pointsMember, setPointsMember] = useState<WalletMember | null>(null);
  const [pointsOperation, setPointsOperation] = useState<'earn' | 'burn' | 'set'>('earn');
  const [pointsAmount, setPointsAmount] = useState<number>(0);
  const [pointsReason, setPointsReason] = useState<string>('');

  // Save columns to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('memberTableColumns', JSON.stringify(columns));
    } catch (error) {
      logger.error('Error saving column preferences:', error);
    }
  }, [columns]);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  // Programs and tiers
  const [programs, setPrograms] = useState<Array<{ id: string; name: string; passkit_program_id?: string; }>>([]);
  const [tiers, setTiers] = useState<Array<{ id: string; name: string; }>>([]);
  const [currentProgramId, setCurrentProgramId] = useState<string>('');

  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    enrolled: 0,
    expired: 0
  });

  const fetchInProgress = useRef(false);

  const loadPrograms = useCallback(async () => {
    try {
      const response: any = await multiTenantMembershipAPI.getPrograms();
      // Backend returns canonical { programs: [], meta: {...} } (adapted by service layer)
      const programsData = Array.isArray(response) ? response : response.programs || response.data || [];
      setPrograms(programsData.map((p: any) => ({ id: p.id, name: p.name })));

      if (programsData.length > 0) {
        setCurrentProgramId(programsData[0].id);
      }
    } catch (error) {
      logger.error('Error loading programs:', error);
      handleApiError(error);
    }
  }, [handleApiError]);

  const loadTiers = useCallback(async (programId: string) => {
    try {
      const response: any = await multiTenantMembershipAPI.getProgramTiers(programId);
      // Backend returns { program_id, program_name, tiers: [], total_tiers: n }
      const tiersData = Array.isArray(response) ? response : response.tiers || [];
      setTiers(tiersData.map((t: any) => ({ id: t.id, name: t.name })));
    } catch (error) {
      logger.error('Error loading tiers:', error);
      handleApiError(error);
    }
  }, [handleApiError]);

  const fetchMembers = useCallback(async () => {
    if (fetchInProgress.current || !currentProgramId) {
      return;
    }

    try {
      fetchInProgress.current = true;
      setLoading(true);

      const filters: MemberListFilters = {
        pageSize: rowsPerPage,
        page: page + 1, // Wallet API uses 1-based pagination
        orderBy: 'created',
        ascending: false
      };

      // Add search filter
      if (searchTerm) {
        filters.searchText = searchTerm;
      }

      // Add tier filter
      if (selectedTier !== 'all') {
        filters.filters = {
          tierId: {
            filterValue: selectedTier,
            filterOperator: 'EQUAL'
          }
        };
      }

      const result = await WalletMembersAPI.listMembers(currentProgramId, filters);

      setMembers(result.members);
      setFilteredMembers(result.members);
      setTotalCount(result.pagination?.totalCount || 0);

      // Update statistics from the result
      setStats({
        total: result.pagination?.totalCount || 0,
        active: result.members.filter((m: any) => m.status === 'ACTIVE').length,
        enrolled: result.members.filter((m: any) => m.status === 'ENROLLED').length,
        expired: result.members.filter((m: any) => m.status === 'EXPIRED').length
      });

      setError(null);

    } catch (error) {
      logger.error('Error fetching members:', error);
      handleApiError(error);
      setError('Greška pri učitavanju članova.');
      setMembers([]);
      setFilteredMembers([]);
    } finally {
      setLoading(false);
      fetchInProgress.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProgramId, page, rowsPerPage, handleApiError]);
  // Note: searchTerm and selectedTier are intentionally NOT in deps
  // They are read directly from state when fetchMembers is called

  // Load programs on component mount
  useEffect(() => {
    loadPrograms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty array = run once on mount

  // Load members when program changes
  useEffect(() => {
    if (currentProgramId) {
      fetchMembers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProgramId, page, rowsPerPage]); // Don't include fetchMembers to avoid infinite loop

  const handleCreateMember = async (memberData: WalletMember) => {
    try {
      setLoading(true);
      await WalletMembersAPI.createMember(memberData);
      showSuccess('Član je uspešno kreiran!');
      setOpenDialog(false);
      setEditingMember(null);
      fetchMembers();
    } catch (error) {
      logger.error('Error creating member:', error);
      handleApiError(error);
      showError('Greška pri kreiranju člana.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateMember = async (memberData: WalletMember) => {
    try {
      setLoading(true);
      await WalletMembersAPI.updateMember(memberData);
      showSuccess('Član je uspešno ažuriran!');
      setOpenDialog(false);
      setEditingMember(null);
      fetchMembers();
    } catch (error) {
      logger.error('Error updating member:', error);
      handleApiError(error);
      showError('Greška pri ažuriranju člana.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMember = async (member: WalletMember) => {
    if (!globalThis.confirm('Da li ste sigurni da želite da obrišete ovog člana?')) {
      return;
    }

    try {
      setLoading(true);
      await WalletMembersAPI.deleteMember({
        id: member.id,
        externalId: member.externalId,
        programId: member.programId
      });
      showSuccess('Član je uspešno obrisan!');
      fetchMembers();
    } catch (error) {
      logger.error('Error deleting member:', error);
      handleApiError(error);
      showError('Greška pri brisanju člana.');
    } finally {
      setLoading(false);
    }
  };

  // Column visibility handlers
  const handleColumnMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setColumnMenuAnchor(event.currentTarget);
  };

  const handleColumnMenuClose = () => {
    setColumnMenuAnchor(null);
  };

  const handleColumnToggle = (columnId: string) => {
    setColumns((prev: TableColumn[]) => prev.map((col: TableColumn) =>
      col.id === columnId ? { ...col, visible: !col.visible } : col
    ));
  };

  const handleColumnReorder = (result: any) => {
    if (!result.destination) return;

    const items = Array.from(columns);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setColumns(items);
  };

  const handleResetColumns = () => {
    setColumns(defaultColumns);
    localStorage.removeItem('memberTableColumns');
    handleColumnMenuClose();
  };

  const handleCopyPassLink = (member: WalletMember) => {
    // Generate PassKit URL - format is https://pub1.pskt.io/{member_id}
    const passUrl = `https://pub1.pskt.io/${member.id || member.externalId}`;

    // Copy to clipboard
    navigator.clipboard.writeText(passUrl).then(() => {
      showSuccess('Link ka passu je kopiran!');
    }).catch((err) => {
      logger.error('Error copying to clipboard:', err);
      showError('Greška pri kopiranju linka.');
    });
  };

  const handleOpenPointsDialog = (member: WalletMember, operation: 'earn' | 'burn' | 'set') => {
    setPointsMember(member);
    setPointsOperation(operation);
    setPointsAmount(operation === 'set' ? (member.points || 0) : 0);
    setPointsReason('');
    setPointsDialogOpen(true);
  };

  const handleClosePointsDialog = () => {
    setPointsDialogOpen(false);
    setPointsMember(null);
    setPointsAmount(0);
    setPointsReason('');
  };

  const handlePointsSubmit = async () => {
    if (!pointsMember) return;

    try {
      setLoading(true);

      // Get program_id (internal UUID)
      const program_id = (pointsMember as any).program_id || currentProgramId;

      // Call backend endpoint for points management
      await api.post(
        `/membership/members/${pointsMember.id}/points`,
        null,
        {
          params: {
            operation: pointsOperation,
            amount: pointsAmount,
            program_id: program_id,
            reason: pointsReason || undefined
          }
        }
      );

      const message = (() => {
        if (pointsOperation === 'earn') return 'dodati';
        if (pointsOperation === 'burn') return 'potrošeni';
        return 'postavljeni';
      })();
      showSuccess(`Poeni su uspešno ${message}!`);
      handleClosePointsDialog();
      fetchMembers();
    } catch (error) {
      logger.error('Error updating points:', error);
      handleApiError(error);
      showError('Greška pri ažuriranju poena.');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedMembers.size === 0) return;

    if (!globalThis.confirm(`Da li ste sigurni da želite da obrišete ${selectedMembers.size} članova?`)) {
      return;
    }

    try {
      setLoading(true);
      const memberIdentifiers = Array.from(selectedMembers).map(memberId => {
        const member = members.find(m => m.id === memberId);
        return {
          id: member?.id,
          externalId: member?.externalId,
        };
      });

      const result = await WalletMembersAPI.bulkDeleteMembers(currentProgramId, memberIdentifiers);

      if (result.success > 0) {
        showSuccess(`Uspešno obrisano ${result.success} članova.`);
      }
      if (result.failed > 0) {
        showError(`Greška pri brisanju ${result.failed} članova.`);
      }

      setSelectedMembers(new Set());
      fetchMembers();
    } catch (error) {
      logger.error('Error bulk deleting members:', error);
      handleApiError(error);
      showError('Greška pri masovnom brisanju članova.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'ENROLLED': return 'primary';
      case 'EXPIRED': return 'error';
      case 'CHECKED_IN': return 'info';
      case 'CHECKED_OUT': return 'warning';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'ENROLLED': return 'Prijavljen';
      case 'ACTIVE': return 'Aktivan';
      case 'SUSPENDED': return 'Suspendovan';
      case 'CANCELLED': return 'Otkazan';
      case 'EXPIRED': return 'Istekao';
      case 'CHECKED_IN': return 'Prijavljen';
      case 'CHECKED_OUT': return 'Odjavljen';
      default: return status;
    }
  };

  const getTierName = (tierId: string | undefined) => {
    if (!tierId) return '-';
    const tier = tiers.find(t => t.id === tierId);
    return tier ? tier.name : tierId;
  };

  // Render cell based on column type
  const renderTableCell = (column: TableColumn, member: WalletMember) => {
    switch (column.id) {
      case 'name':
        return (
          <TableCell key={column.id}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar>
                {member.person?.forename?.[0] || member.person?.emailAddress?.[0] || 'M'}
              </Avatar>
              <Typography variant="subtitle2">
                {member.person?.displayName || `${member.person?.forename || ''} ${member.person?.surname || ''}`.trim() || 'Nepoznato'}
              </Typography>
            </Stack>
          </TableCell>
        );
      case 'email':
        return (
          <TableCell key={column.id}>
            {member.person?.emailAddress && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <EmailIcon fontSize="small" color="action" />
                <Typography variant="caption">{member.person.emailAddress}</Typography>
              </Stack>
            )}
          </TableCell>
        );
      case 'phone':
        return (
          <TableCell key={column.id}>
            {member.person?.mobileNumber && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <PhoneIcon fontSize="small" color="action" />
                <Typography variant="caption">{member.person.mobileNumber}</Typography>
              </Stack>
            )}
          </TableCell>
        );
      case 'externalId':
        return (
          <TableCell key={column.id}>
            <Typography variant="caption">{member.externalId || '-'}</Typography>
          </TableCell>
        );
      case 'tier':
        return (
          <TableCell key={column.id}>
            <Typography variant="caption">{getTierName(member.tierId)}</Typography>
          </TableCell>
        );
      case 'status':
        return (
          <TableCell key={column.id}>
            <Chip
              label={getStatusLabel(member.status || 'ENROLLED')}
              color={getStatusColor(member.status || 'ENROLLED') as any}
              size="small"
            />
          </TableCell>
        );
      case 'points':
        return (
          <TableCell key={column.id}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2" fontWeight="medium">
                {member.points || 0}
              </Typography>
              <Tooltip title="Upravljaj poenima">
                <IconButton
                  size="small"
                  onClick={() => handleOpenPointsDialog(member, 'earn')}
                >
                  <EditCircleIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </TableCell>
        );
      case 'program':
        return (
          <TableCell key={column.id}>
            <Typography variant="caption">{(member as any).program_name || '-'}</Typography>
          </TableCell>
        );
      case 'created':
        return (
          <TableCell key={column.id}>
            <Typography variant="caption" color="text.secondary">
              {member.created ? new Date(member.created).toLocaleDateString('sr-RS') : '-'}
            </Typography>
          </TableCell>
        );
      case 'actions':
        return (
          <TableCell key={column.id} align="right">
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Tooltip title="Kopiraj link ka passu">
                <IconButton
                  size="small"
                  onClick={() => handleCopyPassLink(member)}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Uredi">
                <IconButton
                  size="small"
                  onClick={() => {
                    setEditingMember(member);
                    setOpenDialog(true);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Obriši">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDeleteMember(member)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </TableCell>
        );
      default:
        return null;
    }
  };

  const handleSelectAll = () => {
    if (selectedMembers.size === filteredMembers.length) {
      setSelectedMembers(new Set());
    } else {
      setSelectedMembers(new Set(filteredMembers.map(m => m.id!)));
    }
  };

  const handleSelectMember = (memberId: string) => {
    const newSelected = new Set(selectedMembers);
    if (newSelected.has(memberId)) {
      newSelected.delete(memberId);
    } else {
      newSelected.add(memberId);
    }
    setSelectedMembers(newSelected);
  };

  // Load tiers when current program changes
  useEffect(() => {
    if (currentProgramId) {
      loadTiers(currentProgramId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProgramId]); // Don't include loadTiers to avoid infinite loop

  // Helper function to render draggable column item (reduces nesting)
  const renderDraggableColumnItem = (column: TableColumn, dragProvided: any, snapshot: any) => (
    <MenuItem
      ref={dragProvided.innerRef}
      {...dragProvided.draggableProps}
      {...dragProvided.dragHandleProps}
      onClick={() => handleColumnToggle(column.id)}
      sx={{
        bgcolor: snapshot.isDragging ? 'action.hover' : 'transparent',
        cursor: 'grab',
        '&:active': {
          cursor: 'grabbing'
        }
      }}
    >
      <ListItemIcon>
        <Checkbox checked={column.visible} />
      </ListItemIcon>
      <ListItemText primary={column.label} />
    </MenuItem>
  );

  return (
    <Box sx={{ p: 3 }}>
      <ResponsivePageHeader
        title="Upravljanje Članovima"
        subtitle="Napredna integracija sa wallet API-jem - bez duplikovanja podataka"
        actions={
          <ModernButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingMember(null);
              setOpenDialog(true);
            }}
            disabled={!currentProgramId}
          >
            Novi Član
          </ModernButton>
        }
      />

      {/* Program Selection */}
      <ModernCard sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel>Izaberite Program</InputLabel>
                <Select
                  value={currentProgramId}
                  label="Izaberite Program"
                  onChange={(e) => setCurrentProgramId(e.target.value)}
                >
                  {programs.map((program) => (
                    <MenuItem key={program.id} value={program.id}>
                      {program.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 8 }}>
              <Typography variant="body2" color="text.secondary">
                Članci se učitavaju direktno iz wallet API-ja za izabrani program.
                Multi-tenant izolacija je obezbeđena kroz backend proxy.
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </ModernCard>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <ModernCard>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <IconContainer color="primary">
                  <PeopleIcon />
                </IconContainer>
                <Box>
                  <Typography variant="h4" color="primary.main">
                    {stats.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Ukupno Članova
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </ModernCard>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <ModernCard>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <IconContainer color="success">
                  <CheckCircleIcon />
                </IconContainer>
                <Box>
                  <Typography variant="h4" color="success.main">
                    {stats.active}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Aktivni Članovi
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </ModernCard>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <ModernCard>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <IconContainer color="info">
                  <PersonIcon />
                </IconContainer>
                <Box>
                  <Typography variant="h4" color="info.main">
                    {stats.enrolled}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Enrollovani
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </ModernCard>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <ModernCard>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <IconContainer color="warning">
                  <TrendingUpIcon />
                </IconContainer>
                <Box>
                  <Typography variant="h4" color="warning.main">
                    {stats.enrolled}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Upisani
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </ModernCard>
        </Grid>
      </Grid>

      {/* Search and Filters */}
      <ModernCard sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <ModernTextField
                fullWidth
                placeholder="Pretražite članove..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    fetchMembers();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Tier</InputLabel>
                <Select
                  value={selectedTier}
                  label="Tier"
                  onChange={(e) => setSelectedTier(e.target.value)}
                >
                  <MenuItem value="all">Svi Tier-ovi</MenuItem>
                  {tiers.map((tier) => (
                    <MenuItem key={tier.id} value={tier.id}>
                      {tier.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <ModernButton
                fullWidth
                variant="outlined"
                startIcon={
                  <Box sx={{ pointerEvents: 'none' }}>
                    {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                  </Box>
                }
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  fetchMembers();
                }}
                disabled={loading}
                type="button"
                component="button"
                sx={{
                  '& > *': {
                    pointerEvents: 'none'
                  }
                }}
              >
                {loading ? 'Osvežavam...' : 'Osveži'}
              </ModernButton>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <ModernButton
                fullWidth
                variant="outlined"
                startIcon={<ViewColumnIcon />}
                onClick={handleColumnMenuOpen}
              >
                Kolone
              </ModernButton>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              {selectedMembers.size > 0 && (
                <ModernButton
                  fullWidth
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleBulkDelete}
                >
                  Obriši ({selectedMembers.size})
                </ModernButton>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </ModernCard>

      {/* Column Selection Menu */}
      <Menu
        anchorEl={columnMenuAnchor}
        open={Boolean(columnMenuAnchor)}
        onClose={handleColumnMenuClose}
        PaperProps={{
          sx: { minWidth: 250 }
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Povucite da promenite redosled
          </Typography>
        </Box>
        <DragDropContext onDragEnd={handleColumnReorder}>
          <Droppable droppableId="columns">
            {(provided) => (
              <Box {...provided.droppableProps} ref={provided.innerRef}>
                {columns.map((column: TableColumn, index: number) => (
                  <Draggable key={column.id} draggableId={column.id} index={index}>
                    {(dragProvided, snapshot) => renderDraggableColumnItem(column, dragProvided, snapshot)}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
        <Box sx={{ borderTop: 1, borderColor: 'divider', mt: 1, pt: 1, px: 2, pb: 1 }}>
          <Button
            fullWidth
            size="small"
            onClick={handleResetColumns}
            sx={{ textTransform: 'none' }}
          >
            Vrati na podrazumevano
          </Button>
        </Box>
      </Menu>

      {/* Error Alert */}
      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}      {/* Members Table */}
      <ModernCard>
        <CardContent sx={{ p: 0 }}>
          {selectedMembers.size > 0 && (
            <Toolbar
              sx={{
                pl: { sm: 2 },
                pr: { xs: 1, sm: 1 },
                bgcolor: alpha(theme.palette.primary.main, 0.1),
              }}
            >
              <Typography
                sx={{ flex: '1 1 100%' }}
                color="inherit"
                variant="subtitle1"
                component="div"
              >
                {selectedMembers.size} izabrano
              </Typography>
              <Tooltip title="Obriši izabrane">
                <IconButton color="error" onClick={handleBulkDelete}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Toolbar>
          )}

          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      indeterminate={selectedMembers.size > 0 && selectedMembers.size < filteredMembers.length}
                      checked={filteredMembers.length > 0 && selectedMembers.size === filteredMembers.length}
                      onChange={handleSelectAll}
                      icon={<RadioButtonUncheckedIcon />}
                      checkedIcon={<CheckCircleOutlineIcon />}
                      indeterminateIcon={<SelectAllIcon />}
                    />
                  </TableCell>
                  {columns.filter((col: TableColumn) => col.visible).map((column: TableColumn) => (
                    <TableCell key={column.id} align={column.id === 'actions' ? 'right' : 'left'}>
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={columns.filter((col: TableColumn) => col.visible).length + 1} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : filteredMembers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.filter((col: TableColumn) => col.visible).length + 1} align="center">
                      <Typography variant="body2" color="text.secondary">
                        {searchTerm || selectedTier !== 'all' ? 'Nema rezultata za zadati filter.' : 'Nema članova za prikazana.'}
                      </Typography>
                    </TableCell>
                    </TableRow>
                  ) : (
                    filteredMembers.map((member) => (
                      <TableRow key={member.id} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            color="primary"
                            checked={selectedMembers.has(member.id!)}
                            onChange={() => handleSelectMember(member.id!)}
                            icon={<RadioButtonUncheckedIcon />}
                            checkedIcon={<CheckCircleOutlineIcon />}
                          />
                        </TableCell>
                        {columns.filter((col: TableColumn) => col.visible).map((column: TableColumn) =>
                          renderTableCell(column, member)
                        )}
                      </TableRow>
                    ))
                  )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(Number.parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Redova po stranici:"
            labelDisplayedRows={({ from, to, count }) => {
              const total = count !== -1 ? count : 'više od ' + to;
              return `${from}–${to} od ${total}`;
            }}
          />
        </CardContent>
      </ModernCard>

      {/* Create/Edit Member Dialog */}
      <MemberDialog
        open={openDialog}
        member={editingMember}
        programs={programs}
        tiers={tiers}
        onClose={() => {
          setOpenDialog(false);
          setEditingMember(null);
        }}
        onSave={editingMember ? handleUpdateMember : handleCreateMember}
      />

      {/* Points Management Dialog */}
      <Dialog
        open={pointsDialogOpen}
        onClose={handleClosePointsDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Upravljanje Poenima
          {pointsMember && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Član: {pointsMember.person?.displayName ||
                     `${pointsMember.person?.forename || ''} ${pointsMember.person?.surname || ''}`.trim()}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Trenutno poena: <strong>{pointsMember?.points || 0}</strong>
              </Typography>
            </Box>

            <FormControl fullWidth>
              <InputLabel>Operacija</InputLabel>
              <Select
                value={pointsOperation}
                label="Operacija"
                onChange={(e) => setPointsOperation(e.target.value as 'earn' | 'burn' | 'set')}
              >
                <MenuItem value="earn">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <AddCircleIcon fontSize="small" color="success" />
                    <span>Dodaj poene (Earn)</span>
                  </Stack>
                </MenuItem>
                <MenuItem value="burn">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <RemoveCircleIcon fontSize="small" color="error" />
                    <span>Potroši poene (Burn)</span>
                  </Stack>
                </MenuItem>
                <MenuItem value="set">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <EditCircleIcon fontSize="small" color="primary" />
                    <span>Postavi tačan broj (Set)</span>
                  </Stack>
                </MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              type="number"
              label={pointsOperation === 'set' ? 'Novi broj poena' : 'Broj poena'}
              value={pointsAmount}
              onChange={(e) => setPointsAmount(Number(e.target.value))}
              inputProps={{ min: 0, step: 1 }}
              helperText={(() => {
                if (pointsOperation === 'set') return 'Unesite tačan broj poena koji želite da postavite';
                if (pointsOperation === 'earn') return 'Unesite broj poena za dodavanje';
                return 'Unesite broj poena za oduzimanje';
              })()}
            />

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {pointsOperation === 'earn' && `Novi ukupan broj: ${(pointsMember?.points || 0) + pointsAmount}`}
                {pointsOperation === 'burn' && `Novi ukupan broj: ${Math.max(0, (pointsMember?.points || 0) - pointsAmount)}`}
                {pointsOperation === 'set' && `Novi ukupan broj: ${pointsAmount}`}
              </Typography>
            </Box>

            <Alert severity="info">
              {pointsOperation === 'earn' && 'Poeni će biti dodati trenutnom stanju.'}
              {pointsOperation === 'burn' && 'Poeni će biti oduzeti od trenutnog stanja.'}
              {pointsOperation === 'set' && 'Trenutni broj poena će biti zamenjen novom vrednošću.'}
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePointsDialog}>Otkaži</Button>
          <Button
            onClick={handlePointsSubmit}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {(() => {
              if (pointsOperation === 'earn') return 'Dodaj Poene';
              if (pointsOperation === 'burn') return 'Potroši Poene';
              return 'Postavi Poene';
            })()}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MembershipManagement;
